import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Cube, ArrowRight, Download, Trash, MagicWand, X, ArrowSquareOut, Copy, UploadSimple } from '@phosphor-icons/react';
import DashboardLayout from '../components/DashboardLayout';
import BrandSettingsSummary from '../components/BrandSettingsSummary';
import ReferenceMediaUpload from '../components/ReferenceMediaUpload';
import ShareSaveButton from '../components/ShareSaveButton';
import IdeaSuggester from '../components/IdeaSuggester';

const API = process.env.REACT_APP_BACKEND_URL;

const ASPECT_RATIOS = [
  { id: '1:1', label: 'Square (1:1)', desc: 'Feed' },
  { id: '4:5', label: 'Portrait (4:5)', desc: 'Feed' },
  { id: '9:16', label: 'Vertical (9:16)', desc: 'Stories / Reels' },
];

export default function VisualAdsPage() {
  const [styles, setStyles] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [selectedStyle, setSelectedStyle] = useState('3d_character_mascot');
  const [headline, setHeadline] = useState('');
  const [subhead, setSubhead] = useState('');
  const [industry, setIndustry] = useState('');
  const [extraPrompt, setExtraPrompt] = useState('');
  const [aspect, setAspect] = useState('1:1');
  const [showPoweredBy, setShowPoweredBy] = useState(true);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [previewAd, setPreviewAd] = useState(null);
  // Zero Friction: Customise → progressive disclosure
  const [showAdvanced, setShowAdvanced] = useState(false);
  // Loaded once from BrandSettingsSummary to drive "Write my headline →"
  const [brandProfile, setBrandProfile] = useState(null);
  const [writingHeadline, setWritingHeadline] = useState(false);

  const handleBrandProfileLoad = (bp) => {
    if (!bp) return;
    setBrandProfile(bp);
    if (!industry && bp.industry) setIndustry(bp.industry);
  };

  const writeHeadlineForMe = async () => {
    setWritingHeadline(true);
    try {
      const brand = brandProfile?.brand_name || 'your brand';
      const niche = brandProfile?.industry || industry || 'business';
      const { data } = await axios.post(`${API}/api/visual-ad/auto-style`, {
        headline: `${brand} headline`, industry: niche,
      });
      // The auto-style endpoint returns the closest style; we layer a sensible default headline.
      setSelectedStyle(data.style || selectedStyle);
      const candidates = [
        `${brand}. Built for ${niche}.`,
        `Meet the new standard in ${niche}.`,
        `Your ${niche} stack just got better.`,
        `${brand}. Designed to stop the scroll.`,
      ];
      setHeadline(candidates[Math.floor(Math.random() * candidates.length)]);
      toast.success(`Headline written · style "${data.label}" picked`);
    } catch {
      toast.error('Could not write a headline. Type one in.');
    } finally {
      setWritingHeadline(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/visual-ad/styles`);
        setStyles(data.styles || []);
        setIndustries(data.industries || []);
      } catch { /* ignore */ }
      try {
        const { data: h } = await axios.get(`${API}/api/visual-ad/list`);
        setHistory(h.ads || []);
      } catch { /* ignore */ }
    })();
    // Prefill from First Look Celebration handoff (sessionStorage)
    try {
      const seedHeadline = sessionStorage.getItem('scl_first_look_headline');
      const seedBrand = sessionStorage.getItem('scl_first_look_brand');
      if (seedHeadline) {
        setHeadline(seedHeadline);
      } else if (seedBrand) {
        setHeadline(`${seedBrand}. Meet the new standard.`);
      }
      if (seedBrand && !subhead) {
        setSubhead(`A premium 3D ad for ${seedBrand}, calibrated to your brand.`);
      }
      // Consume — never re-fire after first land
      sessionStorage.removeItem('scl_first_look_headline');
      sessionStorage.removeItem('scl_first_look_brand');
      sessionStorage.removeItem('scl_first_look_url');
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const generate = async () => {
    if (!headline.trim()) { toast.error('Add a headline'); return; }
    setLoading(true);
    setResult(null);
    try {
      const { data } = await axios.post(`${API}/api/visual-ad/generate`, {
        style: selectedStyle, headline, subhead, industry, extra_prompt: extraPrompt,
        aspect_ratio: aspect, show_powered_by: showPoweredBy,
      });
      setResult(data);
      setHistory(prev => [data, ...prev]);
      toast.success('Visual ad ready!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Generation failed');
    } finally {
      setLoading(false);
    }
  };

  const deleteAd = async (id) => {
    try {
      await axios.delete(`${API}/api/visual-ad/${id}`);
      setHistory(prev => prev.filter(a => a.ad_id !== id));
      toast.success('Deleted');
    } catch { toast.error('Failed to delete'); }
  };

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-8">
      <BrandSettingsSummary fields={['brand','industry','color']} onLoad={handleBrandProfileLoad} />
        {/* Header */}
        <div>
          <h1 className="text-4xl sm:text-5xl font-['Instrument_Serif',serif]" style={{ color: '#f5f0e8' }}>
            Visual Ads
          </h1>
          <p className="mt-2 text-base" style={{ color: 'rgba(245,240,232,0.5)' }}>
            Premium 3D & editorial ads — branded with your colors and logo. Pick a style, write a headline, ship.
          </p>
        </div>

        {/* Style picker — horizontal-scroll cards with "Auto-match my brand" first */}
        <div className="rounded-2xl p-6" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.05)' }}>
          <p className="text-xs uppercase tracking-widest font-semibold mb-4" style={{ color: 'rgba(245,240,232,0.4)' }}>Pick a style</p>

          <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1" data-testid="va-style-scroller"
            style={{ scrollSnapType: 'x mandatory', scrollbarWidth: 'thin' }}>
            {/* "Auto-match my brand" pill — always first */}
            <button
              onClick={async () => {
                try {
                  const seedHeadline = headline.trim() || `${brandProfile?.brand_name || 'your brand'} headline`;
                  const seedIndustry = industry || brandProfile?.industry || '';
                  const { data } = await axios.post(`${API}/api/visual-ad/auto-style`, {
                    headline: seedHeadline, industry: seedIndustry,
                  });
                  setSelectedStyle(data.style);
                  toast.success(`Matched "${data.label}" to your brand`);
                } catch { toast.error('Auto-match failed'); }
              }}
              data-testid="auto-style-btn"
              className="shrink-0 text-left p-4 rounded-xl transition-all min-w-[200px]"
              style={{
                background: 'rgba(200,240,96,0.08)',
                border: '1px solid rgba(200,240,96,0.3)',
                scrollSnapAlign: 'start',
              }}>
              <div className="flex items-center justify-between mb-2">
                <MagicWand size={18} weight="fill" style={{ color: '#c8f060' }} />
              </div>
              <p className="text-sm font-semibold mb-1" style={{ color: '#c8f060' }}>Auto-match my brand</p>
              <p className="text-[10px] line-clamp-2" style={{ color: 'rgba(245,240,232,0.55)' }}>The closest style to your brand, picked instantly.</p>
            </button>

            {styles.map(s => (
              <button key={s.id} onClick={() => setSelectedStyle(s.id)} data-testid={`style-${s.id}`}
                className="shrink-0 text-left p-4 rounded-xl transition-all min-w-[200px]"
                style={{
                  background: selectedStyle === s.id ? 'rgba(200,240,96,0.08)' : 'rgba(255,255,255,0.02)',
                  border: `1px solid ${selectedStyle === s.id ? 'rgba(200,240,96,0.25)' : 'rgba(255,255,255,0.05)'}`,
                  scrollSnapAlign: 'start',
                }}>
                <div className="flex items-center justify-between mb-2">
                  <Cube size={18} style={{ color: selectedStyle === s.id ? '#c8f060' : 'rgba(245,240,232,0.4)' }} />
                  {selectedStyle === s.id && <div className="w-2 h-2 rounded-full" style={{ background: '#c8f060' }} />}
                </div>
                <p className="text-sm font-semibold mb-1" style={{ color: selectedStyle === s.id ? '#c8f060' : '#f5f0e8' }}>{s.label}</p>
                <p className="text-[10px] line-clamp-2" style={{ color: 'rgba(245,240,232,0.4)' }}>{s.ideal_for}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Form */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-4 rounded-2xl p-6" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.05)' }}>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-xs uppercase tracking-widest font-semibold" style={{ color: 'rgba(245,240,232,0.4)' }}>What's your ad about?</label>
                <button onClick={writeHeadlineForMe} disabled={writingHeadline} data-testid="va-write-headline-link"
                  className="text-[11px] font-semibold uppercase tracking-wider"
                  style={{ color: '#c8f060', fontFamily: 'ui-monospace, monospace' }}>
                  {writingHeadline ? 'Writing…' : 'Write my headline →'}
                </button>
              </div>
              <input type="text" value={headline} onChange={e => setHeadline(e.target.value)} data-testid="va-headline"
                placeholder="e.g., introducing my new product, a limited offer, why choose us"
                className="w-full bg-zinc-900 border border-white/10 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-white/30"
                style={{ color: '#f5f0e8' }} />
            </div>

            <div>
              <label className="block text-xs uppercase tracking-widest font-semibold mb-2" style={{ color: 'rgba(245,240,232,0.4)' }}>Format</label>
              <div className="flex gap-2 flex-wrap">
                {ASPECT_RATIOS.map(a => (
                  <button key={a.id} onClick={() => setAspect(a.id)} data-testid={`aspect-${a.id}`}
                    className="px-3 py-2 rounded-lg text-xs font-medium transition-all"
                    style={{
                      background: aspect === a.id ? 'rgba(200,240,96,0.08)' : 'rgba(255,255,255,0.03)',
                      border: `1px solid ${aspect === a.id ? 'rgba(200,240,96,0.25)' : 'rgba(255,255,255,0.06)'}`,
                      color: aspect === a.id ? '#c8f060' : 'rgba(245,240,232,0.4)',
                    }}>
                    {a.label}
                  </button>
                ))}
              </div>
            </div>

            <ReferenceMediaUpload
              label="Add reference media (optional)"
              note="Upload a product photo or brand image to incorporate into the ad."
              acceptedTypes={['image']}
              maxFiles={3}
              maxSizeMB={25}
            />

            <button onClick={generate} disabled={loading || !headline.trim()} data-testid="va-generate-btn"
              className="w-full py-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ background: '#c8f060', color: '#080808' }}>
              {loading ? (
                <><div className="w-4 h-4 border-2 border-black/20 border-t-black rounded-full animate-spin" /> Building your ad… (~30s)</>
              ) : (
                <><MagicWand size={16} weight="fill" /> Make this ad &middot; 6 credits</>
              )}
            </button>

            <button onClick={() => setShowAdvanced(v => !v)} data-testid="va-customise-toggle"
              className="w-full text-left px-4 py-2.5 rounded-xl text-[11px] font-semibold flex items-center justify-between"
              style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', color: 'rgba(245,240,232,0.55)' }}>
              <span style={{ fontFamily: 'ui-monospace, monospace', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                {showAdvanced ? 'Hide customise' : 'Customise →'}
              </span>
              <span style={{ color: '#c8f060' }}>{showAdvanced ? '−' : '+'}</span>
            </button>

            {showAdvanced && (
              <div className="space-y-4 pt-1">
                <div className="flex justify-end -mb-1">
                  <IdeaSuggester creator="visual_ad" extraContext={industry}
                    onPick={(idea) => {
                      if (idea.headline) setHeadline(idea.headline);
                      if (idea.subhead) setSubhead(idea.subhead);
                      if (idea.style) setSelectedStyle(idea.style);
                    }} compact label="Suggest ideas" />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest font-semibold mb-2" style={{ color: 'rgba(245,240,232,0.4)' }}>Subheadline (optional)</label>
                  <input type="text" value={subhead} onChange={e => setSubhead(e.target.value)} data-testid="va-subhead"
                    placeholder="e.g., Comparison of top productivity tools"
                    className="w-full bg-zinc-900 border border-white/10 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-white/30"
                    style={{ color: '#f5f0e8' }} />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest font-semibold mb-2" style={{ color: 'rgba(245,240,232,0.4)' }}>Industry / niche override</label>
                  <input type="text" value={industry} onChange={e => setIndustry(e.target.value)} data-testid="va-industry"
                    placeholder="e.g., saas, ecommerce, fitness, fashion"
                    list="industries"
                    className="w-full bg-zinc-900 border border-white/10 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-white/30"
                    style={{ color: '#f5f0e8' }} />
                  <datalist id="industries">
                    {industries.map(i => <option key={i} value={i} />)}
                  </datalist>
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest font-semibold mb-2" style={{ color: 'rgba(245,240,232,0.4)' }}>Extra direction (optional)</label>
                  <textarea value={extraPrompt} onChange={e => setExtraPrompt(e.target.value)} data-testid="va-extra-prompt"
                    placeholder="Mood, characters, key visual elements, things to avoid…"
                    rows={2}
                    className="w-full bg-zinc-900 border border-white/10 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-white/30 resize-none"
                    style={{ color: '#f5f0e8' }} />
                </div>
                <label className="flex items-center gap-3 cursor-pointer pt-1">
                  <input type="checkbox" checked={showPoweredBy} onChange={e => setShowPoweredBy(e.target.checked)} data-testid="va-powered-by" />
                  <span className="text-xs" style={{ color: 'rgba(245,240,232,0.6)' }}>Add "Powered by [my brand]" tag (top-right)</span>
                </label>
              </div>
            )}
          </div>

          {/* Result */}
          <div className="rounded-2xl p-6" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.05)' }}>
            {result ? (
              <div className="space-y-3">
                <p className="text-xs uppercase tracking-widest font-semibold" style={{ color: '#c8f060' }}>Your ad</p>
                <div className="rounded-xl overflow-hidden" style={{ background: '#0a0a0a', aspectRatio: aspect.replace(':', '/') }}>
                  <img src={`${API}${result.image_url}`} alt="" className="w-full h-full object-cover" data-testid="va-result-image" />
                </div>
                <div className="flex gap-2 flex-wrap items-center">
                  <ShareSaveButton url={result.image_url}
                    filename={`${result.headline.slice(0, 40).replace(/[^a-z0-9]/gi, '_')}.png`}
                    title={result.headline}
                    text={`Made with ScalitOS — ${result.style_label}`}
                    dataTestId="va-result" />
                  <button onClick={() => navigator.clipboard.writeText(`${API}${result.image_url}`)}
                    className="px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5"
                    style={{ background: 'rgba(255,255,255,0.04)', color: '#f5f0e8', border: '1px solid rgba(255,255,255,0.08)' }}>
                    <Copy size={12} /> Copy URL
                  </button>
                </div>
              </div>
            ) : (
              <div className="h-full min-h-[260px] flex flex-col items-center justify-center text-center" style={{ color: 'rgba(245,240,232,0.3)' }}>
                <Cube size={48} weight="duotone" />
                <p className="text-sm mt-3">Pick a style and write your headline. Your ad will appear here.</p>
              </div>
            )}
          </div>
        </div>

        {/* History */}
        {history.length > 0 && (
          <div>
            <p className="text-xs uppercase tracking-widest font-semibold mb-4" style={{ color: 'rgba(245,240,232,0.4)' }}>Recent ads</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {history.map(ad => (
                <div key={ad.ad_id} className="rounded-xl overflow-hidden group cursor-pointer relative" data-testid={`va-history-${ad.ad_id}`}
                  style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.05)' }}
                  onClick={() => setPreviewAd(ad)}>
                  <div style={{ aspectRatio: '1/1' }}>
                    <img src={`${API}${ad.image_url}`} alt="" className="w-full h-full object-cover" loading="lazy" />
                  </div>
                  <div className="p-2.5">
                    <p className="text-xs font-medium line-clamp-1" style={{ color: '#f5f0e8' }}>{ad.headline}</p>
                    <p className="text-[10px]" style={{ color: 'rgba(245,240,232,0.4)' }}>{ad.style_label}</p>
                  </div>
                  <button onClick={(e) => { e.stopPropagation(); deleteAd(ad.ad_id); }}
                    className="absolute top-2 right-2 p-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity" style={{ background: 'rgba(0,0,0,0.6)' }}>
                    <Trash size={11} style={{ color: '#ff6b6b' }} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Preview modal */}
        <AnimatePresence>
          {previewAd && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setPreviewAd(null)} data-testid="va-preview-modal"
              className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto" style={{ background: 'rgba(0,0,0,0.85)' }}>
              <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }} onClick={e => e.stopPropagation()}
                className="max-w-2xl w-full rounded-2xl overflow-hidden" style={{ background: '#0a0a0a', border: '1px solid rgba(255,255,255,0.08)' }}>
                <img src={`${API}${previewAd.image_url}`} alt="" className="w-full" />
                <div className="p-5 space-y-3">
                  <p className="text-lg font-semibold" style={{ color: '#f5f0e8' }}>{previewAd.headline}</p>
                  <p className="text-xs" style={{ color: 'rgba(245,240,232,0.5)' }}>{previewAd.style_label} · {previewAd.aspect_ratio}</p>
                  <div className="flex gap-2 flex-wrap items-center">
                    <ShareSaveButton url={previewAd.image_url}
                      filename={`ad-${previewAd.ad_id.slice(0, 8)}.png`}
                      title={previewAd.headline}
                      text={`Made with ScalitOS — ${previewAd.style_label}`}
                      dataTestId="va-preview" />
                    <button onClick={() => setPreviewAd(null)}
                      className="px-4 py-2 rounded-lg text-xs font-semibold" style={{ background: 'rgba(255,255,255,0.04)', color: '#f5f0e8', border: '1px solid rgba(255,255,255,0.08)' }}>
                      Close
                    </button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </DashboardLayout>
  );
}
